import { useMemo, useState } from 'react'
import {
	Box,
	Button,
	Chip,
	IconButton,
	InputAdornment,
	Paper,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	TextField,
	Tooltip,
	Typography,
} from '@mui/material'
import { Add, Business, Delete, Edit, Search } from '@mui/icons-material'
import { DeleteConfirmationDialog } from '../DeleteConfirmationDialog'
import { CustomerFormDialog } from './CustomerFormDialog'
import { CustomerDetailDialog } from './CustomerDetailDialog'

export function CustomersView({
	customers,
	operationLoading,
	onCreateCustomer,
	onUpdateCustomer,
	onDeleteCustomer,
	onToggleWebhook,
	onToggleSensorDelivery,
	onToggleSensorRealtimeEnabled,
	onToggleSensorApiEnabled,
	onUpdateSensorVisibility,
	onCreateSensor,
	onUpdateSensor,
	onDeleteSensor,
	onTransferSensor,
	currentAssignedCustomerId,
	refreshAuthState,
}) {
	const [search, setSearch] = useState('')
	const [detailCustomerId, setDetailCustomerId] = useState(null)
	const [formDialog, setFormDialog] = useState({ open: false, initial: null })
	const [deleteDialog, setDeleteDialog] = useState({ open: false, id: null, name: '' })
	const [actionLoading, setActionLoading] = useState(null)

	const filtered = useMemo(() => {
		const q = search.toLowerCase()
		return customers.filter(c =>
			c.name.toLowerCase().includes(q) ||
			c.sensor_configs?.some(s => s.sensor_id.toLowerCase().includes(q))
		)
	}, [customers, search])

	const handleCreate = async (formData, sensors) => {
		try {
			setActionLoading('create')
			await onCreateCustomer({ ...formData, sensors })
			setFormDialog({ open: false, initial: null })
		} finally {
			setActionLoading(null)
		}
	}

	const handleUpdate = async (formData) => {
		const id = formDialog.initial.id
		try {
			setActionLoading('update')
			await onUpdateCustomer(id, formData)
			if (String(id) === String(currentAssignedCustomerId)) {
				await refreshAuthState()
			}
			setFormDialog({ open: false, initial: null })
		} finally {
			setActionLoading(null)
		}
	}

	const handleUpdateFromDetail = async (id, formData) => {
		try {
			setActionLoading('update')
			await onUpdateCustomer(id, formData)
			if (String(id) === String(currentAssignedCustomerId)) {
				await refreshAuthState()
			}
		} finally {
			setActionLoading(null)
		}
	}

	const handleDelete = async () => {
		try {
			setActionLoading(`delete-${deleteDialog.id}`)
			await onDeleteCustomer(deleteDialog.id)
			setDeleteDialog({ open: false, id: null, name: '' })
			if (detailCustomerId === deleteDialog.id) setDetailCustomerId(null)
		} finally {
			setActionLoading(null)
		}
	}

	return (
		<Box>
			<Box display="flex" justifyContent="space-between" alignItems="center" mb={2.5} gap={2}>
				<TextField
					size="small"
					placeholder="Search customers or sensors…"
					value={search}
					onChange={(e) => setSearch(e.target.value)}
					sx={{ width: 320 }}
					InputProps={{
						startAdornment: (
							<InputAdornment position="start">
								<Search fontSize="small" />
							</InputAdornment>
						),
					}}
				/>
				<Button
					variant="contained"
					startIcon={<Add />}
					onClick={() => setFormDialog({ open: true, initial: null })}
				>
					New Customer
				</Button>
			</Box>

			<TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
				<Table>
					<TableHead>
						<TableRow sx={{ '& th': { fontWeight: 600, color: 'text.secondary', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em' } }}>
							<TableCell>Customer</TableCell>
							<TableCell>Sensors</TableCell>
							<TableCell>Webhooks</TableCell>
							<TableCell>Branding</TableCell>
							<TableCell>Last Updated</TableCell>
							<TableCell align="right">Actions</TableCell>
						</TableRow>
					</TableHead>
					<TableBody>
						{filtered.length === 0 && (
							<TableRow>
								<TableCell colSpan={6} align="center" sx={{ py: 6 }}>
									<Typography color="text.secondary">
										{search ? 'No customers match your search.' : 'No customers yet.'}
									</Typography>
								</TableCell>
							</TableRow>
						)}
						{filtered.map((customer) => (
							<TableRow
								key={customer.id}
								hover
								sx={{ cursor: 'pointer' }}
								onClick={() => setDetailCustomerId(customer.id)}
							>
								<TableCell>
									<Box display="flex" alignItems="center" gap={1.5}>
										<Business fontSize="small" sx={{ color: 'text.secondary' }} />
										<Box>
											<Typography variant="body2" fontWeight="bold">
												{customer.name}
											</Typography>
											{String(customer.id) === String(currentAssignedCustomerId) && (
												<Typography variant="caption" color="primary">
													Your assignment
												</Typography>
											)}
										</Box>
									</Box>
								</TableCell>
								<TableCell>
									<Chip label={customer.sensor_configs?.length ?? 0} size="small" variant="outlined" />
								</TableCell>
								<TableCell>
									<Chip
										label={customer.webhook_enabled ? 'Enabled' : 'Disabled'}
										size="small"
										color={customer.webhook_enabled ? 'success' : 'default'}
									/>
								</TableCell>
								<TableCell>
									<Chip
										label={customer.show_branding ? 'Visible' : 'Hidden'}
										size="small"
										variant="outlined"
									/>
								</TableCell>
								<TableCell>
									<Typography variant="body2" color="text.secondary">
										{customer.updated_at ? new Date(customer.updated_at).toLocaleDateString() : '—'}
									</Typography>
								</TableCell>
								<TableCell align="right" onClick={(e) => e.stopPropagation()}>
									<Box display="flex" gap={0.5} justifyContent="flex-end">
										<Tooltip title="Edit customer">
											<IconButton
												size="small"
												onClick={() => setFormDialog({ open: true, initial: customer })}
											>
												<Edit fontSize="small" />
											</IconButton>
										</Tooltip>
										<Tooltip title="Delete customer">
											<IconButton
												size="small"
												color="error"
												onClick={() => setDeleteDialog({ open: true, id: customer.id, name: customer.name })}
											>
												<Delete fontSize="small" />
											</IconButton>
										</Tooltip>
									</Box>
								</TableCell>
							</TableRow>
						))}
					</TableBody>
				</Table>
			</TableContainer>

		<CustomerDetailDialog
			customerId={detailCustomerId}
			customers={customers}
			open={detailCustomerId !== null}
			onClose={() => setDetailCustomerId(null)}
			onUpdateCustomer={handleUpdateFromDetail}
			onDelete={(id, name) => setDeleteDialog({ open: true, id, name })}
			onToggleWebhook={onToggleWebhook}
			onToggleSensorDelivery={onToggleSensorDelivery}
			onToggleSensorRealtimeEnabled={onToggleSensorRealtimeEnabled}
			onToggleSensorApiEnabled={onToggleSensorApiEnabled}
			onUpdateSensorVisibility={onUpdateSensorVisibility}
		onCreateSensor={onCreateSensor}
		onUpdateSensor={onUpdateSensor}
		onDeleteSensor={onDeleteSensor}
		onTransferSensor={onTransferSensor}
		operationLoading={operationLoading}
		/>

		<CustomerFormDialog
			open={formDialog.open}
			onClose={() => setFormDialog({ open: false, initial: null })}
			onSubmit={formDialog.initial ? handleUpdate : handleCreate}
			initial={formDialog.initial}
			loading={actionLoading === 'create' || actionLoading === 'update'}
			customers={customers}
		/>

			<DeleteConfirmationDialog
				open={deleteDialog.open}
				onClose={() => setDeleteDialog({ open: false, id: null, name: '' })}
				onConfirm={handleDelete}
				title="Delete Customer"
				itemName={deleteDialog.name}
				itemType="customer"
			/>
		</Box>
	)
}
